import { h, init, classModule, styleModule, eventListenersModule, attributesModule, propsModule } from "snabbdom";
import type { VNode } from "snabbdom";
import type { Node as PmNode } from "prosemirror-model";
import type { EditorView } from "prosemirror-view";
import type { CardGroup, Msg, ViewConfig } from "../types";
import { listCards, type ListParams } from "../api";

// Cap on cards fetched for list/kanban views (network is uncapped).
const VIEW_LIMIT = 100;
import { viewListView } from "../view/views/list";
import { viewKanbanView } from "../view/views/kanban";
import { viewNetworkView } from "../view/views/network";
import { formFromAttrs } from "./view-block";

const patch = init([classModule, styleModule, eventListenersModule, attributesModule, propsModule]);

const liveViews = new Set<ViewBlockNodeView>();

export function refreshAllViewBlocks(): void {
  liveViews.forEach((v) => v.fetchAndRender());
}

function attrsToViewConfig(attrs: Record<string, unknown>): ViewConfig {
  const base: ViewConfig = {
    type: (attrs["viewType"] as ViewConfig["type"]) ?? "list",
  };
  if (attrs["filter"]) base.filter = attrs["filter"] as string;
  if (attrs["orderBy"]) base.orderBy = attrs["orderBy"] as string;
  if (typeof attrs["orderDesc"] === "boolean") base.orderDesc = attrs["orderDesc"];
  if (attrs["groupBy"]) base.groupBy = attrs["groupBy"] as string;
  if (Array.isArray(attrs["columns"])) base.columns = attrs["columns"] as string[];
  if (typeof attrs["showAllColumns"] === "boolean") base.showAllColumns = attrs["showAllColumns"];
  const displayVal = attrs["display"] as ViewConfig["display"] | undefined;
  if (displayVal) base.display = displayVal;
  return base;
}

export class ViewBlockNodeView {
  dom: HTMLElement;
  private inner: HTMLElement;
  private vnode: VNode | HTMLElement;
  private config: ViewConfig;
  private attrs: Record<string, unknown>;
  private dispatch: (msg: Msg) => void;
  private pmView: EditorView;
  private getPos: () => number | undefined;

  constructor(
    node: PmNode,
    pmView: EditorView,
    getPos: () => number | undefined,
    dispatch: (msg: Msg) => void,
    // A live view block is in the editor of a card. A preview view block is in
    // a read-only preview at a nesting depth; the live editor is depth 0.
    private place: { kind: "live"; cardId: string } | { kind: "preview"; depth: number }
  ) {
    this.dispatch = dispatch;
    this.pmView = pmView;
    this.getPos = getPos;
    this.attrs = node.attrs as Record<string, unknown>;
    this.config = attrsToViewConfig(this.attrs);

    // Outer wrapper — ProseMirror owns this
    this.dom = document.createElement("div");
    this.dom.className = "view-block-container";
    this.dom.contentEditable = "false";

    // Edit button — only in the live editor, not in read-only previews.
    if (place.kind === "live") {
      const { cardId } = place;
      const editBtn = document.createElement("button");
      editBtn.className = "view-block-edit-btn";
      editBtn.type = "button";
      editBtn.title = "Edit view";
      editBtn.textContent = "✎";
      editBtn.addEventListener("mousedown", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.dispatch({
          type: "OpenViewBlockDialog",
          dialog: { cardId, mode: "edit", pos: this.getPos() ?? null, form: formFromAttrs(this.attrs) },
        });
      });
      this.dom.appendChild(editBtn);
    }

    // Inner container — snabbdom owns this
    this.inner = document.createElement("div");
    this.dom.appendChild(this.inner);
    this.vnode = this.inner;

    liveViews.add(this);
    this.fetchAndRender();
  }

  update(node: PmNode): boolean {
    this.attrs = node.attrs as Record<string, unknown>;
    this.config = attrsToViewConfig(this.attrs);
    this.fetchAndRender();
    return true;
  }

  ignoreMutation(): boolean {
    return true;
  }

  destroy(): void {
    liveViews.delete(this);
    // Run the destroy hooks of the card previews, which stop their editors.
    this.vnode = patch(this.vnode, h("div"));
  }

  fetchAndRender(): void {
    const c = this.config;
    const params: ListParams = {};
    if (c.filter) params.filter = c.filter;
    // List/Kanban order (default updated_at desc) and cap the payload. Network
    // shows the whole graph — no order, no limit.
    if (c.type !== "network") {
      params.orderBy = c.orderBy ?? "updated_at";
      params.orderDesc = c.orderBy ? !!c.orderDesc : true;
      params.limit = VIEW_LIMIT;
    }
    // Kanban groups into columns; network clusters by group.
    if ((c.type === "kanban" || c.type === "network") && c.groupBy) {
      params.groupBy = c.groupBy;
    }
    listCards(params)
      .then((groups) => {
        // A response that arrives after destroy() must not render new previews.
        if (liveViews.has(this)) this.renderGroups(groups);
      })
      .catch((err: unknown) => {
        if (!liveViews.has(this)) return;
        const message = err instanceof Error ? err.message : "";
        // A 422 is an expression problem the user must see; anything else
        // (e.g. backend unavailable in tests) is ignored.
        if (message.startsWith("422")) this.renderError(stripStatus(message));
      });
  }

  private renderGroups(groups: CardGroup[]): void {
    const config = this.config;
    // Cards shown inside this view block are previews one level deeper.
    const childDepth = this.place.kind === "live" ? 1 : this.place.depth + 1;
    let vnode: VNode;

    switch (config.type) {
      case "list":
        vnode = viewListView(config, groups[0]?.cards ?? [], this.dispatch, childDepth);
        break;
      case "kanban":
        vnode = viewKanbanView(config, groups, this.dispatch, childDepth);
        break;
      case "network":
        vnode = viewNetworkView(config, groups, this.dispatch, childDepth);
        break;
    }

    this.vnode = patch(this.vnode, vnode);
  }

  private renderError(message: string): void {
    const vnode = h("div.view-error", [
      h("span.view-error__icon", "⚠"),
      h("span.view-error__text", message),
    ]);
    this.vnode = patch(this.vnode, vnode);
  }
}

// Strip the "<status> <statusText>: " prefix the api layer adds, leaving just
// the backend's human-readable reason.
function stripStatus(message: string): string {
  return message.replace(/^\d+\s[^:]*:\s*/, "");
}
